import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import WorkflowProgress from './WorkflowProgress';
import { TicketData } from '../TicketDetail';
import AttachmentTab from '../../../components/AttachmentTab';
import TabLoadingSpinner from '../../../components/TabLoadingSpinner';
import RichTextEditor from '../../../components/RichTextEditor';
import RichTextDisplay from '../../../components/RichTextDisplay';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { stripHtml, sanitizeNoteInput, hasActualTextContent } from '../../../utils/sanitize';
import {
  CalendarIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { SreInvestigateActions } from '../../../features/sre-investigation';
import type { TicketEntityType } from '../../../features/sre-investigation';
import { resolveTicketAlertSource } from '../../../features/sre-investigation/utils/ticketIdentity';

function mapTicketTypeToEntityType(ticketType?: string | null): TicketEntityType {
  switch ((ticketType || '').toUpperCase()) {
    case 'INCIDENT':
      return 'incident';
    case 'ALERT':
      return 'alert';
    case 'CASE':
    case 'SERVICE_REQUEST':
    default:
      return 'case';
  }
}

interface UnifiedTicketTemplateProps {
  ticket: TicketData;
  isReadOnly: boolean;
  activeTab: 'details' | 'approvals' | 'notes' | 'activity' | 'assessment' | 'resolution' | 'attachments';
  setActiveTab: (tab: 'details' | 'approvals' | 'notes' | 'activity' | 'assessment' | 'resolution' | 'attachments') => void;
  onRefresh: () => void;
}

const priorityColors: Record<string, string> = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200',
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-blue-100 text-blue-800 border-blue-200'
};

const priorityLabels: Record<string, string> = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning',
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
};

const UnifiedTicketTemplate: React.FC<UnifiedTicketTemplateProps> = ({
  ticket,
  isReadOnly,
  activeTab,
  setActiveTab,
  onRefresh
}) => {
  const { user, isContact } = useAuth();
  const { formatDate, formatDateTime } = useDateFormatter();

  // Debug: Log ticket data for CASE and SERVICE_REQUEST tickets
  if (ticket.ticketType === 'CASE' || ticket.ticketType === 'SERVICE_REQUEST') {
    console.log('Ticket Type:', ticket.ticketType);
    console.log('Ticket Data:', ticket);
    console.log('cabRequired:', ticket.cabRequired);
    console.log('approvalGroup:', ticket.approvalGroup);
    console.log('Customer:', ticket.Customer);
    console.log('Engagement:', ticket.Engagement);
    console.log('ServicePlan:', ticket.ServicePlan);
    console.log('Resource:', ticket.Resource);
  }
  const queryClient = useQueryClient();
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalModalData, setApprovalModalData] = useState<{decision: 'approved' | 'rejected', comments: string}>({decision: 'approved', comments: ''});

  // Customer approval modal states
  const [showCustomerApprovalResponseModal, setShowCustomerApprovalResponseModal] = useState(false);
  const [customerApprovalDecision, setCustomerApprovalDecision] = useState<'approved' | 'rejected'>('approved');
  const [customerApprovalComments, setCustomerApprovalComments] = useState('');

  // Customer approval response mutation
  const customerApprovalMutation = useMutation({
    mutationFn: async (data: { decision: 'approved' | 'rejected', comments: string }) => {
      const response = await api.post(`/cases/${ticket.id}/customer-approval/respond`, {
        decision: data.decision,
        comments: data.comments
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      setShowCustomerApprovalResponseModal(false);
      setCustomerApprovalComments('');
      toast.success('Approval response submitted successfully');
      onRefresh();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to process customer approval');
    }
  });

  // Helper function to check if change state is authorize or higher
  const isChangeStateAuthorizeOrHigher = (changeState?: string) => {
    const authorizeOrHigherStates = ['authorize', 'scheduled', 'implement', 'review', 'closed'];
    return changeState ? authorizeOrHigherStates.includes(changeState.toLowerCase()) : false;
  };

  // Fetch approval groups only when the Approvals tab is open
  useQuery({
    queryKey: ['groups', 'approval'],
    queryFn: async () => {
      const response = await api.get('/groups?type=approval');
      return response.data;
    },
    enabled: activeTab === 'approvals' && (ticket.ticketType === 'CHANGE' || ticket.ticketType === 'SERVICE_REQUEST')
  });

  // Fetch members of the approval group
  const { data: approvalGroupMembersData } = useQuery({
    queryKey: ['group-members', ticket.approvalGroup?.id],
    queryFn: async () => {
      if (!ticket.approvalGroup?.id) return [];
      const response = await api.get(`/groups/${ticket.approvalGroup.id}/members`);
      return response.data;
    },
    enabled: activeTab === 'approvals' && (ticket.ticketType === 'CHANGE' || ticket.ticketType === 'SERVICE_REQUEST') && !!ticket.approvalGroup?.id
  });

  // Fetch change or case details with approvals
  const { data: approvalDetails } = useQuery({
    queryKey: [ticket.ticketType === 'CHANGE' ? 'change-approvals' : 'case-approvals', ticket.id],
    queryFn: async () => {
      const endpoint = ticket.ticketType === 'CHANGE' ? `/changes/${ticket.id}` : `/cases/${ticket.id}`;
      const response = await api.get(endpoint);
      return response.data;
    },
    enabled: activeTab === 'approvals' && (ticket.ticketType === 'CHANGE' || ticket.ticketType === 'SERVICE_REQUEST')
  });

  // Approval mutation
  const approvalMutation = useMutation({
    mutationFn: async (data: { decision: 'approved' | 'rejected', comments: string }) => {
      const endpoint = ticket.ticketType === 'CHANGE' ? `/changes/${ticket.id}/approval` : `/cases/${ticket.id}/approval`;
      const response = await api.post(endpoint, {
        decision: data.decision,
        comments: data.comments
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      queryClient.invalidateQueries({ queryKey: [ticket.ticketType === 'CHANGE' ? 'change-approvals' : 'case-approvals', ticket.id] });
      setShowApprovalModal(false);
      onRefresh();
    },
    onError: (error: any) => {
      console.error('Approval error:', error);
      alert(error.response?.data?.message || 'Failed to process approval');
    }
  });

  // Contact work note state
  const [contactWorkNote, setContactWorkNote] = useState('');

  const { data: workNotes = [], isLoading: isLoadingNotes } = useQuery({
    queryKey: ['case', ticket.id, 'notes'],
    queryFn: async () => {
      const response = await api.get(`/cases/${ticket.id}/work-notes`);
      return response.data;
    },
    enabled: activeTab === 'notes',
  });

  const { data: caseActivities = [], isLoading: isLoadingActivities } = useQuery({
    queryKey: ['case-activities', ticket.id],
    queryFn: async () => {
      const response = await api.get(`/cases/${ticket.id}/activities`);
      return response.data;
    },
    enabled: activeTab === 'activity' && !isContact(),
  });

  // Check if ticket is in an active state (not closed/resolved/cancelled/auto_resolved)
  const closedStates = ['closed', 'resolved', 'cancelled', 'closed_complete', 'closed_incomplete', 'auto_resolved'];
  const isTicketActive = !closedStates.includes(ticket.state?.toLowerCase() || '');

  // Contact can add work notes and attachments if ticket is active
  const canContactAddNotes = isContact() && isTicketActive;
  const canContactAddAttachments = isContact() && isTicketActive;

  // Work note mutation for contact users
  const addWorkNoteMutation = useMutation({
    mutationFn: async (note: string) => {
      const response = await api.post(`/cases/${ticket.id}/work-notes`, {
        note: note,
        isPublic: true // Contact notes are always public
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      queryClient.invalidateQueries({ queryKey: ['case', ticket.id, 'notes'] });
      setContactWorkNote('');
      toast.success('Work note added successfully');
      onRefresh();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add work note');
    }
  });

  const handleAddWorkNote = () => {
    const sanitizedNote = sanitizeNoteInput(contactWorkNote);
    if (!hasActualTextContent(sanitizedNote)) {
      toast.error('Please enter a note');
      return;
    }
    addWorkNoteMutation.mutate(sanitizedNote);
  };

  // Check approval status
  const getApprovalStatus = () => {
    if (!approvalDetails?.approvals) return { approved: 0, rejected: 0, pending: 0 };

    const approvals = approvalDetails.approvals;
    const approved = approvals.filter((a: any) => a.state === 'approved').length;
    const rejected = approvals.filter((a: any) => a.state === 'rejected').length;
    const pending = approvals.filter((a: any) => a.state === 'pending').length;

    return { approved, rejected, pending };
  };

  const approvalStatus = getApprovalStatus();

  // Format change type
  const formatChangeType = (type: string | undefined): string => {
    if (!type) return 'Not specified';
    const changeTypeMap: Record<string, string> = {
      'standard': 'Standard',
      'normal': 'Normal',
      'emergency': 'Emergency'
    };
    return changeTypeMap[type.toLowerCase()] || type;
  };

  // Format risk
  const formatRisk = (risk: string | undefined): string => {
    if (!risk) return 'Not specified';
    const riskMap: Record<string, string> = {
      'high': 'High',
      'medium': 'Medium',
      'moderate': 'Medium',
      'low': 'Low'
    };
    return riskMap[risk.toLowerCase()] || risk;
  };

  // Format impact
  const formatImpact = (impact: string | undefined): string => {
    if (!impact) return 'Not specified';
    // Handle format like "low_3" or just "low"
    if (impact.includes('_')) {
      const [level] = impact.split('_');
      return level.charAt(0).toUpperCase() + level.slice(1);
    }
    return impact.charAt(0).toUpperCase() + impact.slice(1);
  };

  // Get type-specific fields based on ticket type
  const renderTypeSpecificFields = () => {
    switch (ticket.ticketType) {
      case 'INCIDENT':
        return (
          <>
            {ticket.resolution && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Resolution</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.resolution} />
                </div>
              </div>
            )}
            {ticket.resolutionCode && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Resolution Code</label>
                <p className="mt-1 text-sm text-gray-900">{ticket.resolutionCode}</p>
              </div>
            )}
          </>
        );
      case 'PROBLEM':
        return (
          <>
            {ticket.problemStatement && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Problem Statement</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.problemStatement} />
                </div>
              </div>
            )}
            {ticket.rootCause && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Root Cause</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.rootCause} />
                </div>
              </div>
            )}
            {ticket.workaround && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Workaround</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.workaround} />
                </div>
              </div>
            )}
          </>
        );
      case 'CHANGE':
        return (
          <>
            {ticket.changeType && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Change Type</label>
                <p className="mt-1 text-sm text-gray-900">{formatChangeType(ticket.changeType)}</p>
              </div>
            )}
            {ticket.changeRisk && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Risk Level</label>
                <p className="mt-1 text-sm text-gray-900">{formatRisk(ticket.changeRisk)}</p>
              </div>
            )}
            {ticket.plannedStartDate && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Planned Start</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.plannedStartDate)}</p>
              </div>
            )}
            {ticket.plannedEndDate && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Planned End</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.plannedEndDate)}</p>
              </div>
            )}
            {ticket.implementationPlan && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Implementation Plan</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.implementationPlan} />
                </div>
              </div>
            )}
          </>
        );
      case 'CASE':
        return (
          <>
            {ticket.channel && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Channel</label>
                <p className="mt-1 text-sm text-gray-900">{ticket.channel}</p>
              </div>
            )}
            {ticket.contactType && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Type</label>
                <p className="mt-1 text-sm text-gray-900">{ticket.contactType}</p>
              </div>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* 1. Workflow Progress at the very top */}
      <WorkflowProgress
        ticketType={ticket.ticketType}
        currentState={ticket.state}
        problemState={ticket.problemState}
        changeState={ticket.changeState}
      />

      {/* Title Section - Below Workflow Progress */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm font-semibold text-gray-600 whitespace-nowrap shrink-0">Title:</span>
          <p className="text-sm text-gray-900 leading-relaxed break-words min-w-0 flex-[1_1_12rem]">
            {stripHtml(ticket.shortDescription) || '-'}
          </p>
          <div className="shrink-0 flex flex-wrap items-center gap-2">
            <SreInvestigateActions
              ticketNumber={ticket.number}
              entityType={mapTicketTypeToEntityType(ticket.ticketType)}
              entityId={ticket.id}
              engagementId={ticket.Engagement?.id}
              title={stripHtml(ticket.shortDescription) || undefined}
              description={typeof ticket.description === 'string' ? stripHtml(ticket.description) : undefined}
              node={ticket.node || ticket.Resource?.name || undefined}
              computer={ticket.node || ticket.Resource?.name || undefined}
              resourceId={
                ticket.Resource?.resourceId ||
                ticket.resourceIdValue ||
                undefined
              }
              resourceGroup={ticket.Resource?.resourceGroupName || undefined}
              vmName={ticket.Resource?.name || undefined}
              ticketState={ticket.state}
              sreInvestigationStatus={ticket.sreInvestigationStatus}
              sreDecisionReason={ticket.sreDecisionReason}
              sreEvaluatedAt={ticket.sreEvaluatedAt}
              sreRunId={ticket.sreRunId}
              customerName={ticket.Customer?.accountName}
              engagementName={ticket.Engagement?.name}
              source={resolveTicketAlertSource(ticket)}
              onPosted={onRefresh}
              forceShow
              autoStart={false}
            />
          </div>
        </div>
      </div>

      {/* Customer Approval Banner - For Contacts */}
      {ticket.customerApprovalRequested &&
       ticket.customerApprovalStatus === 'pending' &&
       isContact() &&
       (user?.id === ticket.contactId || (!ticket.contactId && user?.engagementId === ticket.Engagement?.id)) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Customer Approval Required</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>{ticket.ticketType === 'PROBLEM' ?
                  'This change request requires your approval. Please review the details and provide your decision.' :
                  'This change request requires your approval. Please review the details and provide your decision.'}</p>
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setCustomerApprovalDecision('approved');
                    setShowCustomerApprovalResponseModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCustomerApprovalDecision('rejected');
                    setShowCustomerApprovalResponseModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Approval Banner - For Customer-Approver Group Members */}
      {ticket.customerApprovalRequested &&
       ticket.customerApprovalStatus === 'pending' &&
       !isContact() &&
       (ticket as any).pendingApprovals?.some((a: any) => a.approverId === user?.id && a.state === 'pending') && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Customer Approval Required</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Waiting for customer approval. Approval request has been sent to the contacts and customer approval group.</p>
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setApprovalModalData({ decision: 'approved', comments: '' });
                    setShowApprovalModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setApprovalModalData({ decision: 'rejected', comments: '' });
                    setShowApprovalModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Approval Banner - Read-only for Other Agents */}
      {ticket.customerApprovalRequested &&
       ticket.customerApprovalStatus === 'pending' &&
       !isContact() &&
       !(ticket as any).pendingApprovals?.some((a: any) => a.approverId === user?.id && a.state === 'pending') && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Customer Approval Required</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Waiting for customer approval. Approval request has been sent to the contacts and customer approval group.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. Main content area with tabs and sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left side: Tabs section */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex flex-wrap">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`py-2 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'details'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Details
                </button>
                {ticket.ticketType === 'PROBLEM' && (
                  <>
                    <button
                      onClick={() => setActiveTab('assessment')}
                      className={`ml-8 py-2 px-4 border-b-2 font-medium text-sm ${
                        activeTab === 'assessment'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Assessment Summary
                    </button>
                    <button
                      onClick={() => setActiveTab('resolution')}
                      className={`ml-8 py-2 px-4 border-b-2 font-medium text-sm ${
                        activeTab === 'resolution'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Resolution Summary
                    </button>
                  </>
                )}
                {(ticket.ticketType === 'CHANGE' && isChangeStateAuthorizeOrHigher(ticket.changeState)) ||
                 (ticket.ticketType === 'SERVICE_REQUEST' && (ticket.cabRequired || ticket.approvalGroup)) ? (
                  <button
                    onClick={() => setActiveTab('approvals')}
                    className={`ml-8 py-2 px-4 border-b-2 font-medium text-sm ${
                      activeTab === 'approvals'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Approvals
                  </button>
                ) : null}
                <button
                  onClick={() => setActiveTab('notes')}
                  className={`ml-8 py-2 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'notes'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Work Notes
                </button>
                {/* Hide Activity tab for contact users */}
                {!isContact() && (
                  <button
                    onClick={() => setActiveTab('activity')}
                    className={`ml-8 py-2 px-4 border-b-2 font-medium text-sm ${
                      activeTab === 'activity'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Activity
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('attachments')}
                  className={`ml-8 py-2 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'attachments'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Attachments
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'details' && (
                <div className="space-y-6">
                  {/* INCIDENT specific layout */}
                  {ticket.ticketType === 'INCIDENT' ? (
                    <>
                      {/* Main Fields */}
                      <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Number</label>
                            <p className="mt-1 text-sm text-gray-900">{ticket.number}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Opened</label>
                            <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.createdAt)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Caller</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.caller
                                ? `${ticket.caller.firstName} ${ticket.caller.lastName}`
                                : 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">State</label>
                            <p className="mt-1 text-sm text-gray-900 capitalize">
                              {ticket.state.replace('_', ' ')}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Priority</label>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                              {priorityLabels[ticket.priority] || ticket.priority}
                            </span>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assignment Group</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.AssignmentGroup?.name || 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.assignedTo
                                ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                                : 'Unassigned'}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Short Description</label>
                            <p className="mt-1 text-sm text-gray-900">{stripHtml(ticket.shortDescription)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <div className="mt-1 text-sm text-gray-900">
                              <RichTextDisplay content={ticket.description || 'No description provided'} />
                            </div>
                          </div>
                        </div>
                      </div>


                      {/* Additional fields if resolution exists */}
                      {(ticket.resolution || ticket.resolutionCode) && (
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Resolution</h3>
                          <div className="space-y-4">
                            {ticket.resolution && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Resolution</label>
                                <div className="mt-1 text-sm text-gray-900">
                                  <RichTextDisplay content={ticket.resolution} />
                                </div>
                              </div>
                            )}
                            {ticket.resolutionCode && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Resolution Code</label>
                                <p className="mt-1 text-sm text-gray-900">{ticket.resolutionCode}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : ticket.ticketType === 'SERVICE_REQUEST' ? (
                    <>
                      {/* Main Fields for SERVICE_REQUEST tickets */}
                      <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Number</label>
                            <p className="mt-1 text-sm text-gray-900">{ticket.number}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Opened</label>
                            <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.createdAt)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Impact</label>
                            <p className="mt-1 text-sm text-gray-900">{formatImpact(ticket.impact)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Urgency</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.urgency ? ticket.urgency.charAt(0).toUpperCase() + ticket.urgency.slice(1).replace('_', ' ') : 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Priority</label>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                              {priorityLabels[ticket.priority] || ticket.priority}
                            </span>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">State</label>
                            <p className="mt-1 text-sm text-gray-900 capitalize">
                              {ticket.state.replace('_', ' ')}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assignment Group</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.AssignmentGroup?.name || 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.assignedTo
                                ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                                : 'Unassigned'}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <div className="mt-1 text-sm text-gray-900">
                              <RichTextDisplay content={ticket.description || 'No description provided'} />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Approval Required</label>
                              <p className="mt-1 text-sm text-gray-900">
                                {ticket.cabRequired ? 'Yes' : 'No'}
                              </p>
                            </div>
                            {ticket.cabRequired && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Approval Group</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.approvalGroup?.name || 'Not specified'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : ticket.ticketType === 'PROBLEM' ? (
                    <>
                      {/* Main Fields for PROBLEM tickets */}
                      <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Number</label>
                            <p className="mt-1 text-sm text-gray-900">{ticket.number}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Opened</label>
                            <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.createdAt)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Reported by</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.caller
                                ? `${ticket.caller.firstName} ${ticket.caller.lastName}`
                                : 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">State</label>
                            <p className="mt-1 text-sm text-gray-900 capitalize">
                              {ticket.problemState ? ticket.problemState.replace('_', ' ') : ticket.state.replace('_', ' ')}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Priority</label>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                              {priorityLabels[ticket.priority] || ticket.priority}
                            </span>
                          </div>
                          <div></div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assignment Group</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.AssignmentGroup?.name || 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.assignedTo
                                ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                                : 'Unassigned'}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Short Description</label>
                            <p className="mt-1 text-sm text-gray-900">{stripHtml(ticket.shortDescription)}</p>
                          </div>
                          {ticket.problemStatement && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Problem Statement</label>
                              <div className="mt-1 text-sm text-gray-900">
                                <RichTextDisplay content={ticket.problemStatement} />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Review Matrix */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                          <h4 className="text-lg font-medium text-gray-900 mb-4">Review Matrix</h4>

                          {!isContact() && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">RCA Presented By</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.rcaPresentedBy
                                    ? `${ticket.rcaPresentedBy.firstName} ${ticket.rcaPresentedBy.lastName}`
                                    : 'Not specified'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Problem Date</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.problemDate ? formatDate(ticket.problemDate) : 'Not specified'}
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Reported Issue</label>
                              <div className="mt-1 text-sm text-gray-900 bg-gray-50 rounded border border-gray-200 p-3 min-h-[80px]">
                                <RichTextDisplay content={ticket.reportedIssue || 'Not specified'} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Actual Service Impact</label>
                              <div className="mt-1 text-sm text-gray-900 bg-gray-50 rounded border border-gray-200 p-3 min-h-[80px]">
                                <RichTextDisplay content={ticket.actualServiceImpact || 'Not specified'} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Brief Analysis</label>
                              <div className="mt-1 text-sm text-gray-900 bg-gray-50 rounded border border-gray-200 p-3 min-h-[80px]">
                                <RichTextDisplay content={ticket.briefAnalysis || 'Not specified'} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Root Cause</label>
                              <div className="mt-1 text-sm text-gray-900 bg-gray-50 rounded border border-gray-200 p-3 min-h-[80px]">
                                <RichTextDisplay content={ticket.rootCause || 'Not specified'} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Detailed Analysis</label>
                              <div className="mt-1 text-sm text-gray-900 bg-gray-50 rounded border border-gray-200 p-3 min-h-[100px]">
                                <RichTextDisplay content={ticket.detailedAnalysis || 'Not specified'} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Corrective/Mitigate Actions</label>
                              <div className="mt-1 text-sm text-gray-900 bg-gray-50 rounded border border-gray-200 p-3 min-h-[80px]">
                                <RichTextDisplay content={ticket.correctiveActions || 'Not specified'} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Chronology of Events</label>
                              <div className="mt-1 text-sm text-gray-900 bg-gray-50 rounded border border-gray-200 p-3 min-h-[100px]">
                                <RichTextDisplay content={ticket.chronologyOfEvents || 'Not specified'} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : ticket.ticketType === 'CASE' ? (
                    <>
                      {/* Main Fields for CASE tickets */}
                      <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Number</label>
                            <p className="mt-1 text-sm text-gray-900">{ticket.number}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Opened</label>
                            <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.createdAt)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Impact</label>
                            <p className="mt-1 text-sm text-gray-900">{formatImpact(ticket.impact)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Urgency</label>
                            <p className="mt-1 text-sm text-gray-900 capitalize">
                              {ticket.urgency ? ticket.urgency.split('_')[0] : 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Priority</label>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                              {priorityLabels[ticket.priority] || ticket.priority}
                            </span>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Channel</label>
                            <p className="mt-1 text-sm text-gray-900">{ticket.channel || 'Not specified'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Category</label>
                            <p className="mt-1 text-sm text-gray-900">{ticket.category || 'Not specified'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">State</label>
                            <p className="mt-1 text-sm text-gray-900 capitalize">
                              {ticket.state.replace(/_/g, ' ')}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assignment Group</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.AssignmentGroup?.name || 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.assignedTo
                                ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                                : 'Unassigned'}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Short Description</label>
                            <p className="mt-1 text-sm text-gray-900">{stripHtml(ticket.shortDescription)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <div className="mt-1 text-sm text-gray-900">
                              <RichTextDisplay content={ticket.description || 'No description provided'} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Original layout for other ticket types */}
                      {/* General Information */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">General Information</h3>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Number</label>
                            <p className="mt-1 text-sm text-gray-900">{ticket.number}</p>
                          </div>
                          {ticket.ticketType === 'CHANGE' ? (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Change Type</label>
                                <p className="mt-1 text-sm text-gray-900">{formatChangeType(ticket.changeType)}</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Risk Assessment</label>
                                <p className="mt-1 text-sm text-gray-900">{formatRisk(ticket.changeRisk)}</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Impact</label>
                                <p className="mt-1 text-sm text-gray-900">{formatImpact(ticket.impact)}</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Priority</label>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                                  {priorityLabels[ticket.priority] || ticket.priority}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Type</label>
                                <p className="mt-1 text-sm text-gray-900">{ticket.ticketType.replace('_', ' ')}</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Priority</label>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                                  {priorityLabels[ticket.priority] || ticket.priority}
                                </span>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Category</label>
                                <p className="mt-1 text-sm text-gray-900">{ticket.category || 'Not specified'}</p>
                              </div>
                              <div className="sm:col-span-2 lg:col-span-3">
                                <label className="block text-sm font-medium text-gray-700">Short Description</label>
                                <p className="mt-1 text-sm text-gray-900">{stripHtml(ticket.shortDescription)}</p>
                              </div>
                              <div className="sm:col-span-2 lg:col-span-3">
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <div className="mt-1 text-sm text-gray-900">
                                  <RichTextDisplay content={ticket.description || 'No description provided'} />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Type-specific fields - not shown for CHANGE type as they're in General Information */}
                      {ticket.ticketType !== 'CHANGE' && renderTypeSpecificFields() && (
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {renderTypeSpecificFields()}
                          </div>
                        </div>
                      )}

                      {/* Assignment Information */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Assignment</h3>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          {ticket.ticketType === 'CHANGE' ? (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Assignment Group</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.AssignmentGroup?.name || 'No group assigned'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.assignedTo
                                    ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                                    : 'Unassigned'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Approval Group</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.approvalGroup?.name || 'Not specified'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">CAB Required</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.cabRequired ? 'Yes' : 'No'}
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.assignedTo
                                    ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                                    : 'Unassigned'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Assignment Group</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.AssignmentGroup?.name || 'No group assigned'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Caller</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.caller
                                    ? `${ticket.caller.firstName} ${ticket.caller.lastName}`
                                    : 'No caller'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Customer</label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {ticket.Customer?.accountName || 'No customer'}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Approvals Tab - For CHANGE tickets in authorize or higher states and SERVICE_REQUEST tickets with approval required */}
              {activeTab === 'approvals' &&
               ((ticket.ticketType === 'CHANGE' && isChangeStateAuthorizeOrHigher(ticket.changeState)) ||
                (ticket.ticketType === 'SERVICE_REQUEST' && (ticket.cabRequired || ticket.approvalGroup))) && (
                <div className="space-y-6">
                  {/* Approval Group Information */}
                  {ticket.approvalGroup?.id && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">Approval Group</h4>
                      <p className="text-sm text-blue-800">
                        {ticket.approvalGroup?.name || 'No approval group assigned'}
                      </p>
                    </div>
                  )}

                  {/* Approval Group Members Table */}
                  {ticket.approvalGroup?.id && approvalGroupMembersData && approvalGroupMembersData.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-base font-medium text-gray-900">Approval Group Members</h4>
                      <div className="overflow-x-auto overflow-y-auto max-h-64 shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                User Name
                              </th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                              </th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {approvalGroupMembersData.map((member: any) => {
                              const memberApproval = approvalDetails?.approvals?.find((a: any) => a.approverId === member.id);
                              const hasAnyRejection = approvalDetails?.approvals?.some((a: any) => a.state === 'rejected');
                              // Super admin approval is when someone NOT in the approval group approves
                              const groupMemberIds = approvalGroupMembersData.map((m: any) => m.id);
                              const hasSuperAdminApproval = approvalDetails?.approvals?.some((a: any) =>
                                a.state === 'approved' && !groupMemberIds.includes(a.approverId)
                              );

                              return (
                                <tr key={member.id}>
                                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                    {member.firstName} {member.lastName}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                    {member.email}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    {memberApproval ? (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                        memberApproval.state === 'approved'
                                          ? 'bg-green-100 text-green-800'
                                          : memberApproval.state === 'rejected'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {memberApproval.state === 'approved' ? 'Approved' :
                                         memberApproval.state === 'rejected' ? 'Rejected' : 'Pending'}
                                      </span>
                                    ) : hasSuperAdminApproval ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Approved by Super Admin
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        Pending
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                                    {((ticket.ticketType === 'CHANGE' && ticket.cabRequired) || ticket.ticketType === 'SERVICE_REQUEST') &&
                                     (!memberApproval || memberApproval.state === 'pending') &&
                                     !hasAnyRejection && !hasSuperAdminApproval &&
                                     ((user as any)?.role?.name === 'super_admin' || member.id === (user as any)?.id) ? (
                                      <div className="flex space-x-1">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setApprovalModalData({decision: 'approved', comments: ''});
                                            setShowApprovalModal(true);
                                          }}
                                          disabled={approvalMutation.isPending || isReadOnly}
                                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                        >
                                          {approvalMutation.isPending ? 'Processing...' : 'Approve'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setApprovalModalData({decision: 'rejected', comments: ''});
                                            setShowApprovalModal(true);
                                          }}
                                          disabled={approvalMutation.isPending || isReadOnly}
                                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                        >
                                          {approvalMutation.isPending ? 'Processing...' : 'Reject'}
                                        </button>
                                      </div>
                                    ) : memberApproval ? (
                                      <div className="text-xs text-gray-500">
                                        {memberApproval.comments && (
                                          <p className="mb-1">{memberApproval.comments}</p>
                                        )}
                                        {memberApproval.approvedAt && (
                                          <p>{formatDate(memberApproval.approvedAt)}</p>
                                        )}
                                      </div>
                                    ) : hasAnyRejection ? (
                                      <div className="text-xs text-red-600">
                                        {ticket.ticketType === 'CHANGE' ? 'Change' : 'Request'} rejected - No further approvals allowed
                                      </div>
                                    ) : hasSuperAdminApproval ? (
                                      <div className="text-xs text-green-600">
                                        {ticket.ticketType === 'CHANGE' ? 'Change' : 'Request'} approved by Super Admin
                                      </div>
                                    ) : (user as any)?.role?.name !== 'super_admin' && member.id !== (user as any)?.id ? (
                                      <div className="text-xs text-gray-500">
                                        Only the assigned user or super admin can approve
                                      </div>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* No Members in Approval Group Message */}
                  {ticket.approvalGroup?.id && approvalGroupMembersData && approvalGroupMembersData.length === 0 && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.12M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.12M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">No Members in Approval Group</h3>
                      <p className="text-sm text-gray-500">
                        The approval group has no members assigned.
                      </p>
                    </div>
                  )}

                  {/* No Approval Group Message */}
                  {!ticket.approvalGroup?.id && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">No Approval Group Assigned</h3>
                      <p className="text-sm text-gray-500">
                        This {ticket.ticketType === 'CHANGE' ? 'change request' : 'service request'} does not have an approval group assigned.
                      </p>
                    </div>
                  )}

                  {/* Overall Approval Summary */}
                  {approvalDetails?.approvals && approvalDetails.approvals.length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Approval Summary</h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            {approvalStatus.approved}
                          </div>
                          <div className="text-sm text-gray-600">Approved</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-red-600">
                            {approvalStatus.rejected}
                          </div>
                          <div className="text-sm text-gray-600">Rejected</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-yellow-600">
                            {approvalStatus.pending}
                          </div>
                          <div className="text-sm text-gray-600">Pending</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Work Notes</h3>

                  {/* Add Work Note Form for Contact Users - At the top */}
                  {canContactAddNotes && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="text-base font-medium text-gray-900 mb-3">Add a Note</h4>
                      <div className="space-y-3">
                        <RichTextEditor
                          value={contactWorkNote}
                          onChange={setContactWorkNote}
                          placeholder="Enter your note here..."
                          minHeight="120px"
                        />
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleAddWorkNote}
                            disabled={addWorkNoteMutation.isPending || !contactWorkNote.trim()}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {addWorkNoteMutation.isPending ? 'Adding...' : 'Add Note'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Existing Work Notes */}
                  {isLoadingNotes ? (
                    <TabLoadingSpinner label="Loading work notes" />
                  ) : workNotes && workNotes.length > 0 ? (
                    <div className="space-y-3">
                      {workNotes
                        .filter((note: any) => isContact() ? note.isPublic : true)
                        .map((note: any, index: number) => (
                        <div key={note.id || index} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center">
                              <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">
                                {note.User?.firstName} {note.User?.lastName}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {formatDate(note.createdAt || note.timestamp)}
                            </span>
                          </div>
                          <div className="mt-2 text-sm text-gray-700">
                            <RichTextDisplay content={note.note || note.message} />
                          </div>
                          {note.isPublic && (
                            <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Public
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No work notes available</p>
                  )}
                </div>
              )}

              {activeTab === 'assessment' && ticket.ticketType === 'PROBLEM' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Assessment Summary</h3>

                  {/* Assessment 1 Summary */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-base font-medium text-gray-900 mb-3">Assessment 1</h4>
                    <div className="text-sm text-gray-700">
                      {ticket.assessment1Summary ? (
                        <RichTextDisplay content={ticket.assessment1Summary} />
                      ) : (
                        <p className="text-gray-500 italic">No assessment summary available</p>
                      )}
                    </div>
                  </div>

                  {/* Assessment 2 Summary */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-base font-medium text-gray-900 mb-3">Assessment 2</h4>
                    <div className="text-sm text-gray-700">
                      {ticket.assessment2Summary ? (
                        <RichTextDisplay content={ticket.assessment2Summary} />
                      ) : (
                        <p className="text-gray-500 italic">No assessment summary available</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'resolution' && ticket.ticketType === 'PROBLEM' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Resolution Summary</h3>

                  {/* Root Cause Analysis */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-base font-medium text-gray-900 mb-3">Root Cause Analysis</h4>
                    <div className="text-sm text-gray-700">
                      <RichTextDisplay content={ticket.rootCause || 'No root cause analysis available'} />
                    </div>
                  </div>

                  {/* Workaround */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-base font-medium text-gray-900 mb-3">Workaround</h4>
                    <div className="text-sm text-gray-700">
                      <RichTextDisplay content={ticket.workaround || 'No workaround documented'} />
                    </div>
                  </div>

                  {/* Resolution */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-base font-medium text-gray-900 mb-3">Resolution</h4>
                    <div className="text-sm text-gray-700">
                      <RichTextDisplay content={ticket.resolution || 'No resolution provided'} />
                    </div>
                  </div>

                  {/* Solution Implemented and Date on same row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={ticket.solutionImplemented || false}
                        disabled
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-not-allowed"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Solution Implemented?
                      </label>
                    </div>

                    {ticket.solutionImplemented && (
                      <div className="text-sm text-gray-700">
                        <span className="font-medium mr-2">Implemented on:</span>
                        {(ticket.implementationDate || ticket.implementedDate)
                          ? formatDateTime(ticket.implementationDate || ticket.implementedDate || '')
                          : ''}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'attachments' && (
                <AttachmentTab
                  entityType="case"
                  entityId={ticket.id}
                  canUpload={!isReadOnly || canContactAddAttachments}
                  canDelete={!isReadOnly || canContactAddAttachments}
                  ticketState={ticket.state}
                />
              )}

              {activeTab === 'activity' && !isContact() && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Activity History</h3>
                  {isLoadingActivities ? (
                    <TabLoadingSpinner label="Loading activity" />
                  ) : caseActivities && caseActivities.length > 0 ? (
                    <div className="space-y-3">
                      {caseActivities.map((activity: any, index: number) => {
                        // Format the activity display based on type
                        const renderActivityDetails = () => {
                          const meta = activity.metadata || {};

                          // Handle STATE_CHANGE activities (check both uppercase and lowercase with underscore)
                          if (activity.activityType === 'STATE_CHANGE' || activity.activityType === 'state_change' || activity.activityType === 'state_update') {
                            // Debug log to see what data we're receiving
                            console.log('STATE_CHANGE activity:', {
                              activityType: activity.activityType,
                              previousState: activity.previousState,
                              newState: activity.newState,
                              metadata: meta,
                              currentTicketState: ticket.state
                            });

                            // Get old state from metadata or activity
                            const oldState = String(meta.previousState || meta.oldState || activity.previousState || 'N/A').replace(/_/g, ' ');

                            // Get new state - prioritize metadata/activity fields, then use current ticket state as fallback
                            // Since this is a state change and the ticket is now in "Cancelled" state,
                            // the most recent state change's new state should be the current state
                            let newState = meta.newState || activity.newState;

                            // If newState is not stored in the activity, use the current ticket state for the most recent activity
                            // This is logical because the current state is the result of the most recent state change
                            if (!newState && index === 0) {
                              newState = ticket.state;
                            }

                            const newStateStr = String(newState || 'cancelled').replace(/_/g, ' ');

                            return (
                              <div className="mt-2 text-sm text-gray-700">
                                <div>
                                  <span className="text-gray-600">Old State:</span>{' '}
                                  <span className="font-medium capitalize">{oldState}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">New State:</span>{' '}
                                  <span className="font-medium capitalize">{newStateStr}</span>
                                </div>
                                {meta.changeType && (
                                  <div>
                                    <span className="text-gray-600">Change Type:</span>{' '}
                                    <span className="font-medium">{meta.changeType.replace(/_/g, ' ')}</span>
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // Handle TICKET_TYPE_CONVERSION activities
                          if (activity.activityType === 'TICKET_TYPE_CONVERSION') {
                            return (
                              <div className="mt-2 text-sm text-gray-700">
                                <div>
                                  Converted from <span className="font-medium">{meta.previousTicketType || 'N/A'}</span> to{' '}
                                  <span className="font-medium">{meta.newTicketType || 'N/A'}</span>
                                </div>
                                {meta.conversionReason && (
                                  <div className="mt-1 text-sm italic text-gray-600">
                                    Reason: {meta.conversionReason}
                                  </div>
                                )}
                                {meta.newState && meta.previousState && meta.newState !== meta.previousState && (
                                  <div className="mt-1 text-sm text-gray-600">
                                    State also changed from <span className="font-medium">{meta.previousState}</span> to{' '}
                                    <span className="font-medium">{meta.newState}</span>
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // Handle ASSIGNMENT_CHANGE activities
                          if (activity.activityType === 'ASSIGNMENT_CHANGE') {
                            return (
                              <div className="mt-2 text-sm text-gray-700">
                                Assignment changed
                                {meta.previousAssignee && (
                                  <div>From: <span className="font-medium">{meta.previousAssignee}</span></div>
                                )}
                                {meta.newAssignee && (
                                  <div>To: <span className="font-medium">{meta.newAssignee}</span></div>
                                )}
                              </div>
                            );
                          }

                          // Handle COMMENT_ADDED activities
                          if (activity.activityType === 'COMMENT_ADDED' || activity.activityType === 'WORK_NOTE_ADDED') {
                            return (
                              <div className="mt-2 text-sm text-gray-700">
                                {activity.activityType === 'COMMENT_ADDED' ? 'Comment added' : 'Work note added'}
                                {meta.isPublic && <span className="ml-2 text-green-600">(Public)</span>}
                                {meta.note && (
                                  <div className="mt-1 p-2 bg-gray-100 rounded text-sm">
                                    <RichTextDisplay content={meta.note} />
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // For other activity types, show a more user-friendly display
                          if (meta && Object.keys(meta).length > 0) {
                            // Filter out technical fields
                            const displayableEntries = Object.entries(meta).filter(
                              ([key]) => !['convertedBy', 'performedBy', 'id'].includes(key)
                            );

                            if (displayableEntries.length > 0) {
                              return (
                                <div className="mt-2 text-sm text-gray-700 space-y-1">
                                  {displayableEntries.map(([key, value]) => {
                                    // Format the key to be more readable
                                    const formattedKey = key
                                      .replace(/([A-Z])/g, ' $1')
                                      .replace(/_/g, ' ')
                                      .replace(/^\w/, c => c.toUpperCase())
                                      .trim();

                                    // Format the value
                                    let formattedValue: string;
                                    if (typeof value === 'boolean') {
                                      formattedValue = value ? 'Yes' : 'No';
                                    } else if (typeof value === 'object') {
                                      formattedValue = JSON.stringify(value);
                                    } else {
                                      formattedValue = String(value);
                                    }

                                    return (
                                      <div key={key}>
                                        <span className="font-medium text-gray-600">{formattedKey}:</span>{' '}
                                        <span className="text-gray-900">
                                          <RichTextDisplay content={formattedValue} />
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }
                          }

                          return null;
                        };

                        return (
                          <div key={activity.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="text-sm font-medium text-gray-900">
                                  {activity.activityType.replace(/_/g, ' ').toUpperCase()}
                                </span>
                                <p className="text-sm text-gray-600 mt-1">
                                  by {activity.User?.firstName} {activity.User?.lastName}
                                </p>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDate(activity.performedAt)}
                              </span>
                            </div>
                            {renderActivityDetails()}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500">No activity history available</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side: Timeline and Related Information */}
        <div className="space-y-6">
          {/* Timeline */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Timeline</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(ticket.createdAt)}</p>
                </div>
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <p className="text-sm text-gray-500">Last Updated</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(ticket.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Related Information */}
          {(ticket.parent || ticket.Engagement || ticket.Resource || ticket.Customer || ticket.ServicePlan || ticket.ticketType === 'INCIDENT' || ticket.ticketType === 'PROBLEM' || ticket.ticketType === 'CASE' || ticket.ticketType === 'SERVICE_REQUEST') && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Related Information</h3>
              <div className="space-y-4">
                {ticket.parent && (
                  <div>
                    <p className="text-sm text-gray-500">Parent Ticket</p>
                    <p className="text-sm font-medium text-gray-900">
                      {ticket.parent.number} - {stripHtml(ticket.parent.shortDescription)}
                    </p>
                  </div>
                )}
                {(ticket.ticketType === 'INCIDENT' || ticket.ticketType === 'CASE' || ticket.ticketType === 'SERVICE_REQUEST') && (
                  <>
                    {/* Always show Customer */}
                    <div>
                      <p className="text-sm text-gray-500">Customer</p>
                      <p className="text-sm font-medium text-gray-900">
                        {ticket.Customer?.accountName || 'Not specified'}
                      </p>
                    </div>
                    {/* Always show Engagement */}
                    <div>
                      <p className="text-sm text-gray-500">Engagement</p>
                      <p className="text-sm font-medium text-gray-900">
                        {ticket.Engagement?.name || 'Not specified'}
                      </p>
                    </div>
                    {/* Always show Service Plan */}
                    <div>
                      <p className="text-sm text-gray-500">Service Plan</p>
                      <p className="text-sm font-medium text-gray-900">
                        {ticket.ServicePlan?.name || 'Not specified'}
                      </p>
                    </div>
                    {/* Always show Resource */}
                    <div>
                      <p className="text-sm text-gray-500">Resource</p>
                      <p className="text-sm font-medium text-gray-900">
                        {ticket.Resource?.name || 'Not specified'}
                      </p>
                    </div>
                  </>
                )}
                {ticket.ticketType === 'PROBLEM' && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500">Customer</p>
                      <p className="text-sm font-medium text-gray-900">
                        {ticket.Customer?.accountName || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Engagement</p>
                      <p className="text-sm font-medium text-gray-900">
                        {ticket.Engagement?.name || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Resource</p>
                      <p className="text-sm font-medium text-gray-900">
                        {ticket.Resource?.name || 'Not specified'}
                      </p>
                    </div>
                  </>
                )}
                {ticket.ticketType !== 'INCIDENT' && ticket.ticketType !== 'PROBLEM' && ticket.ticketType !== 'CASE' && ticket.ticketType !== 'SERVICE_REQUEST' && (
                  <>
                    {ticket.Customer && ticket.ticketType === 'CHANGE' && (
                      <div>
                        <p className="text-sm text-gray-500">Customer</p>
                        <p className="text-sm font-medium text-gray-900">{ticket.Customer.accountName}</p>
                      </div>
                    )}
                    {ticket.Engagement && (
                      <div>
                        <p className="text-sm text-gray-500">Engagement</p>
                        <p className="text-sm font-medium text-gray-900">{ticket.Engagement.name}</p>
                      </div>
                    )}
                    {ticket.Resource && (
                      <div>
                        <p className="text-sm text-gray-500">Resource</p>
                        <p className="text-sm font-medium text-gray-900">{ticket.Resource.name}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      <Transition.Root show={showApprovalModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowApprovalModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                        approvalModalData.decision === 'approved' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {approvalModalData.decision === 'approved' ? (
                          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                          </svg>
                        )}
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          {approvalModalData.decision === 'approved'
                            ? `Approve ${ticket.ticketType === 'CHANGE' ? 'Change Request' : 'Service Request'}`
                            : `Reject ${ticket.ticketType === 'CHANGE' ? 'Change Request' : 'Service Request'}`}
                        </Dialog.Title>
                        <div className="mt-4">
                          <label htmlFor="approval-comments" className="block text-sm font-medium text-gray-700">
                            Comments (optional)
                          </label>
                          <RichTextEditor
                            value={approvalModalData.comments}
                            onChange={(value) => setApprovalModalData(prev => ({ ...prev, comments: value }))}
                            placeholder="Enter your comments..."
                            minHeight="120px"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                        approvalModalData.decision === 'approved'
                          ? 'bg-green-600 hover:bg-green-500'
                          : 'bg-red-600 hover:bg-red-500'
                      }`}
                      onClick={() => {
                        approvalMutation.mutate({
                          decision: approvalModalData.decision,
                          comments: approvalModalData.comments
                        });
                      }}
                      disabled={approvalMutation.isPending}
                    >
                      {approvalMutation.isPending ? 'Processing...' : (approvalModalData.decision === 'approved' ? 'Approve' : 'Reject')}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => setShowApprovalModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Customer Approval Response Modal */}
      <Transition appear show={showCustomerApprovalResponseModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowCustomerApprovalResponseModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="sm:flex sm:items-start">
                    <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                      customerApprovalDecision === 'approved' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {customerApprovalDecision === 'approved' ? (
                        <CheckIcon className="h-6 w-6 text-green-600" />
                      ) : (
                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                        </svg>
                      )}
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        {customerApprovalDecision === 'approved' ? 'Approve Fix' : 'Reject Fix'}
                      </Dialog.Title>
                      <div className="mt-4">
                        <label htmlFor="customer-approval-comments" className="block text-sm font-medium text-gray-700 mb-2">
                          Comments {customerApprovalDecision === 'rejected' && <span className="text-red-600">*</span>}
                        </label>
                        <RichTextEditor
                          value={customerApprovalComments}
                          onChange={(value) => setCustomerApprovalComments(value)}
                          placeholder={customerApprovalDecision === 'approved' ? 'Add optional comments...' : 'Please explain why you are rejecting this fix...'}
                          minHeight="120px"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 mt-4">
                    <button
                      type="button"
                      disabled={customerApprovalDecision === 'rejected' && !customerApprovalComments.trim()}
                      className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                        customerApprovalDecision === 'approved'
                          ? 'bg-green-600 hover:bg-green-500 disabled:bg-green-300'
                          : 'bg-red-600 hover:bg-red-500 disabled:bg-red-300'
                      } disabled:cursor-not-allowed`}
                      onClick={() => {
                        customerApprovalMutation.mutate({
                          decision: customerApprovalDecision,
                          comments: customerApprovalComments
                        });
                      }}
                    >
                      {customerApprovalDecision === 'approved' ? 'Confirm Approval' : 'Confirm Rejection'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        setShowCustomerApprovalResponseModal(false);
                        setCustomerApprovalComments('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default UnifiedTicketTemplate;